import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, Phone } from "lucide-react";
import { api } from "@/lib/api";

export default function GiftCard() {
  const { token } = useParams();
  const [g, setG] = useState(null);
  const [portrait, setPortrait] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/gifts/public/${token}`).then((r) => setG(r.data)).catch(() => setError("Gift not found."));
    api.get("/branding").then((r) => { if (r.data.portrait_url) setPortrait(`${process.env.REACT_APP_BACKEND_URL}${r.data.portrait_url}`); }).catch(() => {});
  }, [token]);

  if (error) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-300">{error} <Link to="/gift" className="dg-link">Buy a gift session</Link></div>;
  if (!g) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-400">Loading…</div>;
  if (!g.code) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-300" data-testid="gift-card-unpaid">This gift hasn't been paid for yet. <Link to={`/gift/${token}`} className="dg-link">Finish payment</Link></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="gift-card-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-slate-400">Print this card, save it as a PDF (choose "Save as PDF" in the print window), or just screenshot it.</p>
        <button type="button" onClick={() => window.print()} className="dg-btn-primary rounded-full px-5 py-2 text-sm flex items-center gap-2" data-testid="gift-card-print"><Printer className="w-4 h-4" /> Print / Save PDF</button>
      </div>

      <div className="gift-card relative overflow-hidden rounded-3xl border-4 border-[#C9A227] bg-[#0B1120] text-white p-8 sm:p-12" data-testid="gift-card">
        <div className="absolute inset-0 dg-grid-bg opacity-40 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center justify-between gap-4">
            <div className="font-display font-extrabold text-2xl sm:text-3xl"><span className="text-[#FFD600]">Dr.</span>Gadget</div>
            <div className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.3em] text-[#FFD600]">Gift certificate</div>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-start gap-6">
            {portrait && (
              <div className="shrink-0 text-center" data-testid="gift-card-portrait">
                <img src={portrait} alt="David — Dr.Gadget" className="w-28 sm:w-32 rounded-2xl ring-2 ring-[#C9A227]/70 shadow-lg" />
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-2">David · Dr.Gadget</div>
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-mono uppercase tracking-widest text-slate-400">This certificate entitles</div>
              <div className="mt-1 text-3xl sm:text-4xl font-extrabold font-display" data-testid="gift-card-recipient">{g.recipient_name}</div>
              <div className="mt-6 text-sm font-mono uppercase tracking-widest text-slate-400">to one</div>
              <div className="mt-1 text-2xl sm:text-3xl font-bold font-display text-[#FFD600]">{g.service_name}</div>
              <div className="text-slate-300 mt-1">expert tech help by phone or video with David — experienced hands-on tech</div>
            </div>
          </div>
          {g.message && <p className="mt-6 border-l-4 border-[#FFD600] pl-4 text-slate-200 italic">“{g.message}”</p>}
          <div className="mt-8 grid sm:grid-cols-2 gap-6 items-end">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Gift code</div>
              <div className="mt-1 inline-block font-mono text-2xl tracking-[0.15em] text-[#FFD600] border border-[#C9A227]/60 rounded-xl px-5 py-3" data-testid="gift-card-code">{g.code}</div>
              <div className="text-xs text-slate-400 mt-2">Never expires</div>
            </div>
            <div className="text-sm text-slate-300">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">How to redeem</div>
              Go to <span className="text-white">{window.location.origin.replace(/^https?:\/\//, "")}/book</span>, pick your session and a time, and enter the code on the details step — nothing to pay.
              <div className="mt-2 flex items-center gap-2 text-white"><Phone className="w-4 h-4 text-[#FFD600]" /> 310-422-0669</div>
            </div>
          </div>
          <div className="mt-8 text-xs text-slate-500">From {g.buyer_name} · Dr.Gadget Tech Solutions & Installation · Los Angeles</div>
        </div>
      </div>
    </div>
  );
}
